import { useContext, useState } from 'react'

import { faCog, faPen } from '@fortawesome/pro-solid-svg-icons'
import { Box, Button, FormControlLabel, Radio, RadioGroup, Switch, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert, AlertTitle } from '@material-ui/lab'
import { CoreEmptyState } from '@scoir/empty-state'
import { ConfirmationDialog } from '@scoir/modals'
import clsx from 'clsx'

import { isDownload, isSFTPActive, isSFTPSlate } from 'common/integrations-helpers'
import SYSTEM_PERMISSIONS from 'common/system-permissions'
import { useAppSelector } from 'store'
import { hasPermission } from 'store/user'

import IntegrationConfigurationContext from '../IntegrationConfigurationProvider/Context'
import many_to_one from './many_to_one_pdf.png'
import one_to_one from './one_to_one_pdf.svg'

const useStyles = makeStyles((theme) => ({
  switch: {
    backgroundColor: theme.palette.pageBackground?.main,
    width: '100%',
    padding: theme.spacing(1),
    margin: theme.spacing(2, 0),
  },
  radioGroup: {
    flexDirection: 'row',
    gridGap: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  radio: {
    flex: 1,
    borderRadius: 4,
    marginLeft: 0,
    marginRight: 0,
    border: `1px solid ${theme.palette.text.disabled}`,
    '& .MuiFormControlLabel-label': {
      width: '100%',
    },
  },
  selected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.pageBackground?.main,
  },
  disabled: {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(1),
  },
  alert: {
    marginTop: theme.spacing(2),
  },
  alertButton: {
    whiteSpace: 'nowrap',
  },
}))

const Documents = () => {
  const classes = useStyles()
  const canManageDocumentDelivery = useAppSelector((state) =>
    hasPermission(state, SYSTEM_PERMISSIONS.PERFORM_ADMISSIONS_TASKS),
  )
  const { state, onActivateDocsToggle, onUpdateBatchTranscriptSetting, onForceNewEasySlateIndexFields } = useContext(
    IntegrationConfigurationContext,
  )
  const isSlateActive = state.Slate?.Status === 'Live'
  const pdfDeliveryValue = state.ConcatenateBatchTranscripts ? 'many-to-one' : 'one-to-one'

  const canOverrideIndex =
    state.CanUseLegacyIndex && (isDownload(state) || (isSFTPActive(state) && !isSFTPSlate(state)))
  const askToUpdate = Boolean(!state.ForceNewEasySlateIndexFields && canOverrideIndex)
  const [open, setOpen] = useState(false)

  if (!canManageDocumentDelivery) {
    return (
      <>
        <Typography variant="h6">Automated Document Delivery</Typography>
        <CoreEmptyState
          condensed
          icon={faPen}
          title="You don’t have permission to manage Automated Document Delivery"
          subTitle="Contact your school’s administrator to grant you the appropriate access."
        />
      </>
    )
  }

  return (
    <>
      <Typography variant="h6">Automated Document Delivery</Typography>
      {!state.ConfiguredDeliveryMethod ? (
        <CoreEmptyState
          condensed
          icon={faCog}
          title="Ready to automate your workflow?"
          subTitle="Configure your integration to enable nightly document delivery."
        />
      ) : (
        <>
          <FormControlLabel
            className={classes.switch}
            label="Enable Scoir document delivery via SFTP"
            control={
              <Switch
                checked={state.isDocsDeliveryEnabled}
                onChange={(e) => {
                  if (!state.ConfiguredDeliveryMethod) return // Safety check for TS - should not see toggle if no configuration type
                  onActivateDocsToggle({ checked: e.target.checked, type: state.ConfiguredDeliveryMethod })
                }}
                color="primary"
              />
            }
          />
          <Typography>How would you like your files created?</Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Scoir will prepare your documents for manual & batch downloads according to your preferences.
          </Typography>
          <RadioGroup
            aria-label="select pdf delivery options"
            name="pdfOptions"
            className={classes.radioGroup}
            value={pdfDeliveryValue}
            onChange={(e) => {
              onUpdateBatchTranscriptSetting({ isBatched: e.target.value === 'many-to-one' })
            }}
          >
            <FormControlLabel
              className={clsx(classes.radio, {
                [classes.selected]: pdfDeliveryValue === 'one-to-one',
                [classes.disabled]: isSlateActive,
              })}
              value="one-to-one"
              control={<Radio color="primary" />}
              label={
                <Box className={classes.radioLabel}>
                  Individual PDFs for each document
                  <img
                    width={150}
                    src={many_to_one}
                    alt="many to one"
                  />
                </Box>
              }
              disabled={isSlateActive}
            />
            <FormControlLabel
              className={`${classes.radio} ${pdfDeliveryValue === 'many-to-one' ? classes.selected : ''}`}
              value="many-to-one"
              control={<Radio color="primary" />}
              label={
                <Box className={classes.radioLabel}>
                  Together as a single PDF
                  <img
                    width={60}
                    src={one_to_one}
                    alt="one to one"
                  />
                </Box>
              }
              disabled={isSlateActive}
            />
          </RadioGroup>
          {isSlateActive && (
            <Alert severity="info">Your preferences have been preconfigured based on Slate's requirements. </Alert>
          )}
          {askToUpdate && (
            <>
              <Alert
                severity="info"
                className={classes.alert}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    className={classes.alertButton}
                    onClick={() => setOpen(true)}
                  >
                    Enable Metadata
                  </Button>
                }
              >
                <AlertTitle>Standardize your data format.</AlertTitle>
                We’ve introduced a more robust file format that includes document metadata. Enable this now to ensure
                your CRM has the most accurate details for every document.
              </Alert>
              <ConfirmationDialog
                open={open}
                headerText="Standardize your data format?"
                onClose={() => setOpen(false)}
                confirmationText="Confirm"
                onConfirm={onForceNewEasySlateIndexFields}
              >
                <Typography>
                  Please confirm that you’d like to enable the expanded data file. Once enabled, you won’t be able to
                  revert to the previous format. Please contact Support with any questions.
                </Typography>
              </ConfirmationDialog>
            </>
          )}
        </>
      )}
    </>
  )
}

export default Documents
